import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AbortContext } from "@home-health-notify/domain";
import { SyncMode } from "@home-health-notify/models";
import {
  ApiErrorHandler,
  Constants,
  OrganizationContext,
  useDebouncedAbortableCall,
  useSyncConfig,
} from "hhn-commons";
import { useSnackbar } from "notistack";

type GroupOption = { id: string; name: string };

export default function EmrSetupForm() {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const { config, isLoading, isSaving, save } = useSyncConfig();

  const [syncMode, setSyncMode] = useState<SyncMode>("manual");
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const [options, setOptions] = useState<GroupOption[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Hydrate editable state from the loaded config (names resolve as options load).
  useEffect(() => {
    if (config) {
      setSyncMode(config.syncMode);
      setSelectedGroups(config.internalGroupIds.map(id => ({ id, name: id })));
    }
  }, [config]);

  // Server-side search against the org's internal (non-patient) groups.
  const searchGroups = useDebouncedAbortableCall(
    async (context: AbortContext, term: string) => {
      const manager = selectedOrgUser?.chatManager.getNonPatientChatManager();
      if (!manager) return;
      try {
        setGroupsLoading(true);
        await manager.fetchChatGroup(term || undefined, true, context);
        setOptions(
          manager
            .getFetchedChats()
            .data.map(group => ({ id: group.id, name: group.name })),
        );
      } finally {
        setGroupsLoading(false);
      }
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  useEffect(() => {
    if (selectedOrgUser) searchGroups("");
    return () => {
      selectedOrgUser?.chatManager.getNonPatientChatManager()?.resetChatGroup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser]);

  // Backfill names for already-selected groups once matching options arrive.
  useEffect(() => {
    setSelectedGroups(prev =>
      prev.map(selected => {
        if (selected.name !== selected.id) return selected;
        return options.find(o => o.id === selected.id) ?? selected;
      }),
    );
  }, [options]);

  const onSave = async () => {
    try {
      await save({
        syncMode,
        internalGroupIds: selectedGroups.map(group => group.id),
      });
      enqueueSnackbar("EMR settings saved", { variant: "success" });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  return (
    <Box sx={{ maxWidth: 620 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        EMR Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Control how patients from your EMR are created in HHN.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Sync mode
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={syncMode}
              onChange={(_, value: SyncMode | null) => {
                if (value) setSyncMode(value);
              }}
              data-testid="sync-mode-toggle"
            >
              <ToggleButton value="manual" data-testid="sync-mode-manual">
                Manual
              </ToggleButton>
              <ToggleButton value="auto" data-testid="sync-mode-auto">
                Automatic
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Manual: every EMR patient waits for review. Automatic: patients
              are created without review.
            </Typography>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Autocomplete
              multiple
              options={options}
              value={selectedGroups}
              loading={groupsLoading}
              filterOptions={x => x}
              getOptionLabel={group => group.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setSelectedGroups(value)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") searchGroups(value);
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Internal groups"
                  placeholder="Search groups"
                  slotProps={{
                    ...params.slotProps,
                    htmlInput: {
                      ...params.slotProps.htmlInput,
                      "data-testid": "internal-groups-input",
                    },
                  }}
                />
              )}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Members of these groups are added to every created patient.
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={isSaving}
            data-testid="emr-setup-save"
          >
            Save
          </Button>
        </Paper>
      )}
    </Box>
  );
}
